import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as api from '../../api/educentialApi';
import {
  BookOpen,
  CheckCircle,
  FileText,
  Eye,
  BookMarked,
  Video,
  File,
  Book,
  Plus
} from 'lucide-react';

const ContentLibrary = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPublished, setFilterPublished] = useState('all');

  useEffect(() => {
    fetchContent();
  }, [filterCategory, filterPublished]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterCategory) filters.category = filterCategory;
      if (filterPublished !== 'all') filters.published = filterPublished === 'published';
      
      const response = await api.getEducatorContent(filters);
      setContent(response.data);
    } catch (err) {
      console.error('Error fetching content:', err);
      setError(err.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) {
      return;
    }

    try {
      await api.deleteContent(contentId);
      await fetchContent();
    } catch (err) {
      console.error('Error deleting content:', err);
      alert('Failed to delete content');
    }
  };

  const handleTogglePublish = async (contentId, currentlyPublished) => {
    try {
      await api.toggleContentPublish(contentId, !currentlyPublished);
      await fetchContent();
    } catch (err) {
      console.error('Error toggling publish:', err);
      alert('Failed to update publish status');
    }
  };

  // Filter by search term
  const filteredContent = content.filter((item) =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchContent}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Content Library</h1>
            <p className="text-gray-600 mt-1">Manage your educational content</p>
          </div>
          <Link
            to="/educator/content/new"
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Content
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            label="Total Content"
            value={content.length}
            icon={BookOpen}
          />
          <StatCard
            label="Published"
            value={content.filter((c) => c.published).length}
            icon={CheckCircle}
          />
          <StatCard
            label="Drafts"
            value={content.filter((c) => !c.published).length}
            icon={FileText}
          />
          <StatCard
            label="Total Views"
            value={content.reduce((sum, c) => sum + c.viewCount, 0)}
            icon={Eye}
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Content
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title or description..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="">All Categories</option>
                <option value="test-prep">Test Preparation</option>
                <option value="overseas">Overseas Education</option>
                <option value="skills">Skill Development</option>
                <option value="career">Career Development</option>
                <option value="general">General</option>
              </select>
            </div>

            {/* Publish Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filterPublished}
                onChange={(e) => setFilterPublished(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Content</option>
                <option value="published">Published Only</option>
                <option value="draft">Drafts Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        {filteredContent.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg">
              {content.length === 0 
                ? 'No content created yet. Create your first piece of content!'
                : 'No content matches your filters'}
            </p>
            {content.length === 0 && (
              <Link
                to="/educator/content/new"
                className="inline-block mt-4 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
              >
                Create Content
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContent.map((item) => (
              <ContentCard
                key={item.id}
                content={item}
                onEdit={() => navigate(`/educator/content/${item.id}/edit`)}
                onDelete={() => handleDelete(item.id)}
                onTogglePublish={() => handleTogglePublish(item.id, item.published)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon }) => {
  return (
    <div className="relative bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 overflow-hidden group">
      <div className="absolute inset-0 bg-teal-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="bg-teal-100 p-3 rounded-lg">
            <Icon className="w-6 h-6 text-teal-600" />
          </div>
        </div>
        <p className="text-sm text-gray-600 font-medium">{label}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
    </div>
  );
};

// Content Card Component
const ContentCard = ({ content, onEdit, onDelete, onTogglePublish }) => {
  const getTypeIcon = (type) => {
    const icons = {
      course: BookMarked,
      resource: File,
      guide: Book,
      video: Video,
    };
    return icons[type] || BookOpen;
  };

  const TypeIcon = getTypeIcon(content.contentType);

  return (
    <div className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="p-3 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg">
            <TypeIcon className="w-6 h-6 text-white" />
          </div>
          <div className="flex gap-2">
            {content.published ? (
              <span className="px-2.5 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                Published
              </span>
            ) : (
              <span className="px-2.5 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">
                Draft
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
          {content.title}
        </h3>
        {content.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-3">{content.description}</p>
        )}

        {/* Meta */}
        <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
            {content.category}
          </span>
          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
            {content.contentType}
          </span>
        </div>

        <div className="text-xs text-gray-500 mb-4">
          <p>Created: {new Date(content.createdAt).toLocaleDateString()}</p>
          <p>Views: {content.viewCount}</p>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onEdit}
            className="flex-1 px-3 py-2 bg-teal-600 text-white text-sm rounded hover:bg-teal-700"
          >
            Edit
          </button>
          <button
            onClick={onTogglePublish}
            className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            {content.published ? 'Unpublish' : 'Publish'}
          </button>
          <button
            onClick={onDelete}
            className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentLibrary;
